import React from "react";
import get from "lodash/get";
import styled, { css } from "styled-components/macro";
import { rem } from "polished";
import { useField } from "formik";
import { SchemaOptionType } from "csv-package";

import Radio from "./Radio";
import FormLabel, { Label } from "./FormLabel";
import FormNote, { Note } from "./FormNote";
import ErrorMessage from "./ErrorMessage";
import ConditionalFieldsWrapper from "./ConditionalFieldsWrapper";
import { Wrapper as QuestionWrapper } from "./Question";
import useQuestionConfig from "../hooks/useQuestionConfig";
import Info from "./Info";

type Props = {
  name: string;
  label?: string;
  note?: string;
  options?: SchemaOptionType[];
  optionChildren?: {
    [key: string]: React.ReactNode;
  };
  indentChildren?: boolean;
  compact?: boolean;
  displayRows?: boolean;
  className?: string;
  descriptionView?: 'infobox' | 'onchecked';
};

const RadioGroup: React.FC<Props> = ({
  name,
  optionChildren,
  indentChildren = true,
  compact,
  displayRows,
  className,
  descriptionView = 'onchecked',
  ...rest
}) => {
  const [field, meta] = useField(name);
  const { label, note, options } = useQuestionConfig(name, rest);

  const hasError = meta.touched && meta.error;

  if (options === undefined) {
    return null;
  }

  return (
    <Wrapper className={className} compact={compact}>
      {label && <FormLabel name={name} text={label} />}
      {note && <FormNote text={note} />}

      {hasError && <StyledErrorMessage>{meta.error}</StyledErrorMessage>}
      <RadioItems displayRows={displayRows}>
        {options.map((o) => {
          const optionChild = get(optionChildren, o.value, null);
          const checked = meta.value === o.value;
          return (
            <RadioWrapper key={o.value} displayRows={displayRows} hasDescription={Boolean(o.description) && descriptionView === 'infobox'}>
              <Radio
                name={name}
                value={o.value}
                label={o.text}
                onRadioChange={field.onChange}
                onRadioBlur={field.onBlur}
                checked={checked}
              />
              {optionChild && (
                <ConditionalFieldsWrapper
                  conditions={{ [name]: o.value }}
                  indent={indentChildren}
                  embeded
                >
                  {optionChild}
                </ConditionalFieldsWrapper>
              )}
              {checked && o.description && descriptionView === 'onchecked' && (
                <DescriptionContainer>
                  <FormNote text={o.description} />
                </DescriptionContainer>
              )}
              {o.description && descriptionView === 'infobox' &&  (
                <InfoWrapper>
                  <Info string={`?|${label}|${o.description}`} />
                </InfoWrapper>
              )}
            </RadioWrapper>
          );
        })}
      </RadioItems>
    </Wrapper>
  );
};

export default RadioGroup;

export const Wrapper = styled.div<{ compact?: boolean }>`
  margin-bottom: ${rem(35)};

  ${({ compact }) =>
    compact &&
    css`
      margin-top: -${rem(16)};
      > ${Note} {
        display: none;
      }
      > ${Label} {
        display: none;
      }
    `}
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 13px;
`;

const RadioWrapper = styled.div<{ displayRows?: boolean, hasDescription?: boolean }>`
  margin-top: ${rem(4)};
  > div > ${QuestionWrapper}:first-child {
    margin-top: 1rem;
  }

  ${({ hasDescription }) =>
    hasDescription &&
    css`
      display: flex;
      align-items: center;
      gap: ${rem(8)};
    `}

  ${({ displayRows }) =>
    displayRows &&
    css`
      width: calc(50% - 2px);
    `}
`;

const RadioItems = styled.div<{ displayRows?: boolean }>`
  ${({ displayRows }) =>
    displayRows &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
    `}
`;

const DescriptionContainer = styled.div`
  margin-top: 1rem;
`;

const InfoWrapper = styled.div`
  display: inline-block;
`;